import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronDown, ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";

const ShowItem = ({ show, onPick, testId }) => (
  <button onClick={() => onPick(show.slug)} className="group/item flex items-center gap-3 w-full rounded-lg px-2 py-1.5 text-left hover:bg-white/[0.06] transition-colors" data-testid={testId}>
    {show.cover_url ? <img src={show.cover_url} alt="" className="h-8 w-8 rounded-md object-cover ring-1 ring-white/10 shrink-0" loading="lazy" /> : <span className="h-8 w-8 rounded-md bg-[#053671] shrink-0" />}
    <span className="text-sm text-slate-200 group-hover/item:text-white truncate">{show.name}</span>
    {(show.kind === "video" || show.kind === "tiktok") && <span className="ml-auto shrink-0 rounded-full bg-rose-500/15 px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wider text-rose-400">Video</span>}
  </button>
);

export const MegaMenu = () => {
  const [groups, setGroups] = useState([]);
  const [open, setOpen] = useState(null);
  const navigate = useNavigate();
  const closeTimer = useRef(null);

  useEffect(() => { api.get("/menu").then((r) => setGroups(r.data)).catch(() => {}); }, []);

  const pick = (slug) => { setOpen(null); navigate(`/shows/${slug}`); };
  const enter = (name) => { clearTimeout(closeTimer.current); setOpen(name); };
  const leave = () => { closeTimer.current = setTimeout(() => setOpen(null), 160); };
  const active = groups.find((g) => g.name === open);
  const total = (g) => g.shows.length + g.subgroups.reduce((n, s) => n + s.shows.length, 0);

  return (
    <div className="relative hidden md:block border-b border-white/10 bg-[#053671]/25 backdrop-blur-xl z-40" onMouseLeave={leave} data-testid="mega-menu">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 flex items-center justify-center gap-1 h-12 overflow-x-auto">
        {groups.map((g) => (
          <button key={g.name} onMouseEnter={() => enter(g.name)} onClick={() => setOpen(open === g.name ? null : g.name)}
            className={`relative flex items-center gap-1 rounded-full px-3.5 py-1.5 text-base font-semibold whitespace-nowrap transition-colors ${open === g.name ? "bg-white/10 text-white" : "text-slate-200 hover:text-white hover:bg-white/5"}`}
            data-testid={`mega-menu-tab-${g.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
            {g.name}
            <ChevronDown size={14} className={`text-sky-400 transition-transform duration-200 ${open === g.name ? "rotate-180" : ""}`} />
            {open === g.name && <span className="absolute -bottom-[7px] left-1/2 -translate-x-1/2 h-1 w-8 rounded-full bg-sky-400" />}
          </button>
        ))}
      </div>

      {active && (
        <div className="absolute inset-x-0 top-full" onMouseEnter={() => enter(active.name)} data-testid="mega-menu-panel">
          <div className="max-w-[1400px] mx-auto px-5 sm:px-8 pb-4">
            <div className="relative overflow-hidden rounded-2xl card-surface grain shadow-2xl shadow-black/60 reveal" style={{ animationDuration: ".25s" }}>
              <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-[#053671] blur-3xl opacity-40 pointer-events-none" />
              <div className="relative p-6">
                <div className="flex items-center justify-between mb-4">
                  <Link to={`/browse/${active.slug}`} onClick={() => setOpen(null)} className="group/head" data-testid="mega-menu-category-link">
                    <h3 className="font-display text-2xl font-extrabold tracking-tight group-hover/head:text-sky-400 transition-colors">{active.name}</h3>
                    <p className="text-xs font-mono text-slate-400 mt-0.5">{total(active)} show{total(active) === 1 ? "" : "s"} · view category</p>
                  </Link>
                  <div className="flex items-center gap-4">
                    <Link to={`/browse/${active.slug}`} onClick={() => setOpen(null)} className="inline-flex items-center gap-1 rounded-full bg-sky-400 px-3.5 py-1.5 text-xs font-bold text-[#070F1E] hover:bg-sky-300 transition-colors" data-testid="mega-menu-explore-button">Explore {active.name} <ArrowUpRight size={14} /></Link>
                    <Link to="/shows" onClick={() => setOpen(null)} className="inline-flex items-center gap-1 text-xs font-semibold text-sky-400 hover:text-sky-300" data-testid="mega-menu-all-shows">All shows <ArrowUpRight size={14} /></Link>
                  </div>
                </div>

                {total(active) === 0 ? (
                  <p className="text-sm text-slate-400 py-6" data-testid="mega-menu-empty">New shows coming soon.</p>
                ) : active.subgroups.length ? (
                  <div className="grid gap-x-6 gap-y-5" style={{ gridTemplateColumns: `repeat(${Math.min(active.subgroups.filter((s) => s.shows.length).length + (active.shows.length ? 1 : 0), 4)}, minmax(0, 1fr))` }}>
                    {active.subgroups.filter((s) => s.shows.length).map((sub) => (
                      <div key={sub.name} data-testid={`mega-menu-subgroup-${sub.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                        <p className="eyebrow mb-2 pl-2">{sub.name}</p>
                        <div className="space-y-0.5">{sub.shows.map((s) => <ShowItem key={s.id} show={s} onPick={pick} testId={`mega-menu-show-${s.slug}`} />)}</div>
                      </div>
                    ))}
                    {active.shows.length > 0 && (
                      <div><p className="eyebrow mb-2 pl-2">More</p><div className="space-y-0.5">{active.shows.map((s) => <ShowItem key={s.id} show={s} onPick={pick} testId={`mega-menu-show-${s.slug}`} />)}</div></div>
                    )}
                  </div>
                ) : (
                  <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-6 gap-y-0.5">
                    {active.shows.map((s) => <ShowItem key={s.id} show={s} onPick={pick} testId={`mega-menu-show-${s.slug}`} />)}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
